import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
//import genericStyles from '../GenericComponents/GenericStyles.module.css';
import * as ccUtils from '../CC/utils';
import styles from './Record.module.css';
import MobileZoomedImage from './MobileZoomedImage';
import ImageWithFallback from '../GenericComponents/ImageWithFallback';

interface RecordProps {
  record: any;
  setAndKeepMainY: (newMainY: number, prevMainY: number) => void;
}

interface Image {
  image: string;
  copyright: string;
  rank: string;
}

function replaceExtensionWithDzi(imageUrl: string): string {
  const urlWithoutExtension = imageUrl.substring(0, imageUrl.lastIndexOf('.'));
  return `${urlWithoutExtension}.dzi`;
}

function Record({ record, setAndKeepMainY }: RecordProps,) {
  const { t } = useTranslation();
  const ctx = useCCContext();
  const filmStripRef = useRef<HTMLDivElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [noImageUrl] = useState<string>(`${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci`); // Set default no image URL as string
  const [filmStripImages, setFilmStripImages] = useState<Image[]>([]);
  const [, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isZoomedImageVisible, setIsZoomedImageVisible] = useState<boolean>(false);

  const showSearch = () => {
    setAndKeepMainY(-200, -300);
  }

  useEffect(() => {
    if (record) {
      const images = Array.isArray(record?.data?.record?.image) ? record?.data?.record?.image : [record?.data?.record?.image];
      const sortedImages = images.filter(Boolean).sort((a: Image, b: Image) => parseInt(a.rank) - parseInt(b.rank)); // Filter out null or undefined images
      setFilmStripImages(sortedImages);
      setCurrentIndex(0);

      if (filmStripRef.current) {
        filmStripRef.current.scrollLeft = 0;
      }


      const defaultImageUrl = sortedImages.length > 0 ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${replaceExtensionWithDzi(sortedImages[0].image)}` : noImageUrl;
      setZoomUrl(defaultImageUrl);

      if (sortedImages.length > 0) {

        let filename = sortedImages[0].image;
        setImageUrl(filename ?
          `${process.env.REACT_APP_IMAGE_BASE_URL}/${filename}&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes` :
          `${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes`);
      } else {
        setImageUrl(`${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes`);
      }

    }
  }, [record]);

  useEffect(() => {
    if (record?.data?.record?.title?.title) {
      document.title = `Collections | Musée des Arts et Metiers - ${record?.data?.record?.title?.title}`;
    }

    return () => {
      document.title = "Collections | Musée des Arts et Metiers";
      //console.log('useEffect close', document.title);
    };
  }, [record]);


  if (!record) {
    return <div>No record selected.</div>;
  }

  function getHyperlinks(record: any): JSX.Element | null {
    const hyperlinks = record?.data?.record?.hyperlink;

    if (!hyperlinks) return null;

    const renderLink = (link: { objnrlink: string; hyperlink: string }) => {
      const linkText = link.objnrlink;
      return (
        <a
          key={link.objnrlink}
          href={link.hyperlink}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.tagValue}
        >
          {linkText}
        </a>
      );
    };

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{t("record_hyperlink")} : </span>
        {Array.isArray(hyperlinks)
          ? hyperlinks.map((link, index) => (
            <span key={link.objnrlink}>
              {renderLink(link)}
              {index < hyperlinks.length - 1 && ', '}
            </span>
          ))
          : renderLink(hyperlinks)}
      </div>
    );
  }

  const handleScrollLeft = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: -300,
      behavior: 'smooth'
    });
  };

  const handleFilmStripImageClick = (image: Image, idx: number) => {
    const newImageFilename = replaceExtensionWithDzi(image.image);
    const newImageUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}/${newImageFilename}`;
    setZoomUrl(newImageUrl);
    setImageUrl(image.image ?
      `${process.env.REACT_APP_IMAGE_BASE_URL}/${image.image}&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes` :
      `${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci&width=1024&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes`);

    setCurrentIndex(idx);
  };

  const handleScrollRight = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: 300,
      behavior: 'smooth'
    });
  };

  function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    let fieldValue = getNestedValue(record?.data?.record, fieldName);

    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      fieldValue = JSON.stringify(fieldValue);
    }

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        <span
          className={styles.tagValue}
          dangerouslySetInnerHTML={{ __html: fieldValue ?? defaultValue }}
        ></span>
      </div>
    );
  }

  function getDefaultRowHyperlink(record: any, label: string, fieldName: string, hyperlinkfield: string = "", defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    //console.log(fieldName);
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
    //console.log(fieldValue);

    const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      ccUtils.customSearch(ctx, '/record/' + hyperlinkfield, '', '' + value + '', label + ' : ' + value);
      showSearch();
    };

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        {fieldValue
          ? <span className={styles.authName} onClick={(e) => handleOnClick(label, fieldValue, e)}>{fieldValue}</span>
          : defaultValue}
      </div>
    );
  }

  function getDefaultRowHyperlinkArray(
    record: any,
    label: string,
    fieldName: string,
    hyperlinkfield: string = "",
    defaultValue: string = ""
    ): JSX.Element | null {
      function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      }
    
      //console.log(fieldName);
      let fieldValue = getNestedValue(record?.data?.record, fieldName);
      //console.log(fieldValue);
    
      const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        ccUtils.customSearch(ctx, '/record/' + hyperlinkfield, '', '' + value + '', label + ' : ' + value);
      };
    
      // Als fieldValue leeg is en er is geen defaultValue, return null
      if (!fieldValue && !defaultValue) return null;
    
      // Zorg ervoor dat fieldValue een array is
      const fieldValuesArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          <div>
            {fieldValuesArray.map((value, index) => (
              <div key={index}>
                <span
                  className={styles.authName}
                  onClick={(e) => handleOnClick(label, value, e)}
                >
                  {value}
                </span>
              </div>
            ))}
            {!fieldValuesArray.length && defaultValue}
          </div>
        </div>
      );
  }

  function getPersonnes(record: any, tag: keyof typeof record.data.record) {
    const elements = record?.data?.record?.[tag];
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];

    elementArray.sort((a, b) => {
      const keyA = a.roletypeid + ',' + a.displayorder;
      const keyB = b.roletypeid + ',' + b.displayorder;
      return keyA.localeCompare(keyB);
    });

    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/record/creator,/record/conxother', '', '[' + name + ']', 'Personnes et institutions : ' + name);
    };

    const rows = elementArray.map((element: any, index) => {
      const { role, [tag]: tagValue, DisplayDate } = element;
      return (
        <div className={styles.row} key={index}>
          <span className={styles.taglabel}>{role} : </span>
          <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
          {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return <>
      <div style={{ marginTop: '5px', marginBottom: '5px' }}>
        {/* {t("record_personnes_institutions")} */}
      </div>
      <div style={{ fontSize: '0.9em' }}>
        {rows}
      </div>
    </>
  }

  function getAuteurs(record: any, tag: keyof typeof record.data.record) {
    const elements = record?.data?.record?.[tag];
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];

    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/record/creator,/record/conxother', '', '[' + name + ']', 'Personnes et institutions : ' + name);
    };

    const rows = elementArray.map((element: any, index) => {
      const { Role, [tag]: tagValue } = element;
      return (
        <div className={styles.row} key={index} >
          <span className={styles.taglabel}>{Role} : </span>
          <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
        </div>
      );
    });

    if (rows.length === 0) return null;

    return <>
      <div>
        {rows}
      </div>
    </>
  }

  try {
    return (
      <div id="pdf-container" className={styles.Record}>
        <div className={`pdfOnly`} style={{ marginBottom: '20px' }}><img src='headerPdf.jpg' style={{ width: '300px' }} alt="CNAM Logo" /></div>
        <div className={styles.ccListviewRecordImage}>
          <div className={styles.UnzoomedImage}>
            {imageUrl && <ImageWithFallback src={imageUrl} alt="Image" placeholder={`${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci&width=1024`} maxWidth={'90vw'} maxHeight={'60vh'} />}
            {false && <div className={styles.UnzoomedImageExpand} onClick={() => setIsZoomedImageVisible(!isZoomedImageVisible)}>
              {isZoomedImageVisible ?
                (<GenericIconButton
                  icon='solid/compress-alt.svg'
                  hoverEnabled={true}
                  width='28px'
                  height='28px'
                  onClick={() => setIsZoomedImageVisible(false)}
                />) :
                (<GenericIconButton
                  icon='solid/expand-alt.svg'
                  hoverEnabled={true}
                  width='28px'
                  height='28px'
                  onClick={() => setIsZoomedImageVisible(true)}
                />)
              }
            </div>}
          </div>
          <div className={styles.ZoomedImage} style={{ display: isZoomedImageVisible ? 'block' : 'none' }}>
            <div className={styles.topRightButton}>
              <GenericIconButton
                icon='solid/compress-alt.svg'
                hoverEnabled={true}
                width='28px'
                height='28px'
                onClick={() => setIsZoomedImageVisible(false)}
              />
            </div>
            {!zoomUrl && noImageUrl && (
              <img
                src={noImageUrl}
                alt="Not available"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '60vh',
                  width: 'auto',
                  height: 'auto',
                  display: 'block'
                }}
              />
            )}
            {zoomUrl && <MobileZoomedImage key={`${zoomUrl}-${isZoomedImageVisible}`} dzi={zoomUrl} downloadAllowed={true} images={filmStripImages.map(img => ({ imagesmore: img.image, copyright: img.copyright, rank: img.rank }))} initialIndex={currentIndex} />}
          </div>
          {filmStripImages.length > 1 && (
            <div className={styles.filmStripContainer}>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-left.svg' width='22px' height='20px' onClick={handleScrollLeft} />
              </div>
              <div className={styles.filmStrip} ref={filmStripRef}>
                {filmStripImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${img.image}&height=100&bg=ffffff&cache=yes`}
                    alt={`Rank ${img.rank}`}
                    className={styles.filmStripImage}
                    onClick={() => handleFilmStripImageClick(img, idx)}
                  />
                ))}
              </div>
              <div className={styles.buttonContainer}>
                <GenericIconButton icon='light/angle-right.svg' width='22px' height='20px' onClick={handleScrollRight} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.ccListviewRecordText}>
        <div className={styles.title}>
            {getDefaultRow(record, t("record_title"), "title.title")}
          </div>
          {getDefaultRow(record, t("record_objectnumber"), "objectnumber")}
          {getDefaultRowHyperlink(record, t("record_classification"), "classification")}
          {/*getDefaultRowHyperlink(record, t("record_classification"), "classification.classification", "classification")*/}
          {getDefaultRowHyperlink(record, t("record_auteur"), "auteur.auteur")}
          {getPersonnes(record, 'conxother')}
          {getAuteurs(record, 'creatorname')}
          {getHyperlinks(record)}
          {/*getDefaultRow(record, t("record_objectname"), "objectname")*/}
          {getDefaultRow(record, t("record_dated"), "dated")}
          {getDefaultRow(record, t("record_accessiondate"), "accessiondate")}
          {getDefaultRow(record, t("record_dimensions"), "dimensions")}
          {getDefaultRow(record, t("record_medium"), "medium")}
          {getDefaultRow(record, t("record_inscribed"), "inscribed")}
          {getDefaultRow(record, t("record_commentairehtml"), "commentairehtml")}
          {getDefaultRowHyperlinkArray(record, t("record_theme"), "thesthemes")}
          {/*getDefaultRow(record, t("record_location"), "location")*/}
          
          <span className={styles.tagValue}>
            <strong>Permalien : </strong>
            {`${process.env.REACT_APP_BASE_URL}?id=${record?.data?.record?.objectnumber}`}
            <button
              onClick={() =>
                navigator.clipboard.writeText(
                  `${process.env.REACT_APP_BASE_URL}?id=${record?.data?.record?.objectnumber}`
                )
              }
              style={{
                marginLeft: "10px",
                padding: "5px 10px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Copie
            </button>
          </span>
{/*       
          Question for Julien: How can I transform this old code to the new format?
          resBuilder.Append("<br /><br /><b>Permalien : </b><br />" + "http://seolane2.cnam.fr/?queries=query=search=N°%20d'inventaire=[" + getRowNoDoubleDotNoBr(ccRecord, "", "record/objectnumber") + "]&showtype=record" + "<br /><br />");
*/}          

{/* 
          Question for Julien: I only want to show location/site and location/description, but i cannot do that. How does this work?
          {getDefaultRow(record, t("record_location"), "location")}
*/}

{/*
          Question for Julien: How can we add this code to the website in it's new format
          // Related records - parent record
                    string relatedchild = HelperProcedures.getNodeValueDef(ccRecord.SelectSingleNode("record/relatedchild"), "");
                    string objectid = HelperProcedures.getNodeValueDef(ccRecord.SelectSingleNode("record/ccobjectid"), "");
                    if (relatedchild != "")
                    {
                        resBuilder.Append("<span class=\"record_subtitleRecord\">Cette notice décrit un ensemble : </span>");
                        resBuilder.Append("<span class=\"relatedButton\">");
                        // resBuilder.Append("<span onclick=\"$('#mainPage').scope().processParameters('query=search=ccobjectid=" + relatedchild + "');\">voir toutes les notices</span>");
                        // resBuilder.Append("<span onclick=\"$('#mainPage').scope().processParameters('query=search=relatedparent=" + objectid + "');\">voir toutes les notices</span>");
                        resBuilder.Append("<span onclick=\"$('#mainPage').scope().processParameters('query=search=relatedid=" + objectid + "');\">voir toutes les notices</span>");
                        resBuilder.Append("<br><br></span>");
                    }
            // End

            // Related records - packages
                    string relatedpackage = getRelatedPackageId(ccRecord);
                    if (relatedpackage != "")
                    {
                          resBuilder.Append("<span class=\"record_subtitleRecord\">Objet(s) en lien </span><br /><br />");
                          resBuilder.Append("<div ng-init=\"processCcDirectives();\" class=\"ccExternalWidget\" data-ccexternal=\"embedAction=slick&amp;embedExternalQuery=" + HttpUtility.UrlEncode("packageid=" + relatedpackage) + "\"></div>"); //relatedpackage="+ relatedpackage +"
                          //resBuilder.Append("<br /><br /><span class=\"ccHyperlink\" onclick=\"$('#mainPage').scope().processParameters('query=search=objectid=" + relatedid + "');\">voir toutes les notices</span>");
                          resBuilder.Append("<br><br></span>");
                    }
            // End
*/}

{/*
          Show only for specific user groups - not yet implemented - after that - Question for Julien I think
          ONLY for "Utilisateurs internes" & "Admin"

          NB! Change to new format: resBuilder.Append(getThesaurusField(ccRecord, "Thèmes", "MAM.AAA", "Thèmes"));
          {getDefaultRow(record, t("record_catrais"), "catrais")}
          {getDefaultRow(record, t("record_bibliography"), "bibliography")}
          {getDefaultRow(record, t("record_related_notices"), "relatedworks")}
          {getDefaultRow(record, t("record_exhibitions"), "exhibitions")}
          {getDefaultRow(record, t("record_catalogueisodate"), "catalogueisodate")}
*/}

{/* Fields not in use (yet?) for CNAM
          {getDefaultRowHyperlink(record, t("record_periode"), "period", "period")}   
          {getDefaultRow(record, t("record_description"), "description")}
          {getDefaultRow(record, t("record_portfolio"), "portfolio")}
          {getDefaultRow(record, t("record_provenance"), "provenance")}
          {getDefaultRow(record, t("record_related"), "relatedworks")}
          {getDefaultRow(record, t("record_exhibitions"), "exhibitions")}
          {getDefaultRow(record, t("record_objectstatus"), "objectstatus")}
          {getDefaultRow(record, t("record_accessionmethod"), "accessionmethod")}
          {getDefaultRow(record, t("record_accessiondate"), "accessiondate")}
          {getDefaultRow(record, t("record_bibliography"), "bibliography")}
          {getDefaultRow(record, t("record_protection"), "protection")}     
          {getDefaultRow(record, t("record_location"), "location")}     
          {getMonumentWebsite(record)}
          {
             //link bij tappiserie records
             //A propos des tapisseries
             //<a href="https://collection.tapisseries.monuments-nationaux.fr" target="_blank" rel="noopener noreferrer">{t('footer_tapestries_collection')} &gt;</a>
          }
*/}     

        </div>
      </div>
    );
  } catch (e) {
    setError(e as any);
    return <div>Error: {(e as any).message}</div>;
  }
}

export default Record;
